import { API_BASE } from '../../config/apiBase';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, Clock, Phone, Shield, ChevronRight, Lock, Sparkles } from 'lucide-react';

const API = API_BASE;

// ==================== STEP 1: CONDITIONALLY APPROVED ====================
const Step1Approved = ({ data, onContinue }) => (
  <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
    <div className="max-w-lg w-full text-center space-y-8">
      {/* Badge */}
      <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-5 py-2">
        <CheckCircle className="w-5 h-5 text-emerald-400" />
        <span className="text-emerald-400 font-semibold text-sm tracking-wide uppercase">Conditionally Approved</span>
      </div>

      {/* Main heading */}
      <div className="space-y-4">
        <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
          Congratulations{data?.customer_first_name ? `, ${data.customer_first_name}` : ''}!
        </h1>
        <p className="text-lg text-gray-300">
          You have been <span className="text-emerald-400 font-semibold">conditionally approved</span> for equipment financing.
        </p>
      </div>

      {/* Equipment card */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardContent className="p-6 text-left space-y-3">
          <p className="text-gray-400 text-sm uppercase tracking-wide">Equipment</p>
          <p className="text-white font-semibold text-lg">{data?.equipment_description || 'Commercial Equipment'}</p>
          <div className="flex justify-between items-center pt-2 border-t border-white/10">
            <span className="text-gray-400">Approved Amount</span>
            <span className="text-2xl font-bold text-white">
              ${(data?.equipment_cost || 0).toLocaleString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* CTA */}
      <Button
        onClick={onContinue}
        size="lg"
        className="w-full h-14 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-red-600/20 transition-all hover:shadow-red-600/40"
        data-testid="continue-btn"
      >
        Continue
        <ChevronRight className="w-5 h-5 ml-2" />
      </Button>

      <p className="text-gray-500 text-xs">
        Inspired Funding LLC - Trusted Commercial Financing Provider
      </p>
    </div>
  </div>
);

// ==================== STEP 2: PAYMENT / ACTIVATION ====================
const Step2Payment = ({ data, onActivateNow, onActivateLater }) => {
  const [timeLeft, setTimeLeft] = useState(data?.seconds_remaining || 0);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(prev => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
  const discountActive = timeLeft > 0 && data?.discount_active;

  const fullFee = data?.terms?.origination_fee || 0;
  const discountedFee = data?.terms?.discounted_origination_fee || fullFee;
  const savings = fullFee - discountedFee;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Activate Your Approval
          </h2>
          <p className="text-gray-400">Complete your origination fee to proceed</p>
        </div>

        {/* Countdown Timer */}
        {discountActive && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center" data-testid="countdown-timer">
            <p className="text-amber-400 text-sm font-medium mb-2">Discounted rate expires in</p>
            <div className="flex justify-center gap-3">
              {[{v: hours, l: 'HRS'}, {v: minutes, l: 'MIN'}, {v: seconds, l: 'SEC'}].map(({v, l}) => (
                <div key={l} className="bg-amber-500/20 rounded-lg px-4 py-2 min-w-[60px]">
                  <span className="text-2xl font-bold text-amber-300 font-mono">{String(v).padStart(2, '0')}</span>
                  <p className="text-amber-400/60 text-xs mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Option 1: Activate Today */}
        <Card className={`border-2 transition-all cursor-pointer ${discountActive ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`} data-testid="activate-today-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-2">
                  {discountActive ? 'SAVE 10% TODAY' : 'ACTIVATE NOW'}
                </Badge>
                <p className="text-white font-medium">Activate Today{discountActive ? ' and Save' : ''}</p>
              </div>
              <div className="text-right">
                {discountActive && (
                  <p className="text-gray-500 line-through text-sm">${fullFee.toLocaleString()}</p>
                )}
                <p className="text-3xl font-bold text-white">
                  ${(discountActive ? discountedFee : fullFee).toLocaleString()}
                </p>
                {discountActive && savings > 0 && (
                  <p className="text-emerald-400 text-sm font-medium">You save ${savings.toLocaleString()}</p>
                )}
              </div>
            </div>

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="confirm-checkbox">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-600 accent-red-600"
              />
              <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                I confirm I want to move forward with this financing
              </span>
            </label>

            <Button
              onClick={onActivateNow}
              disabled={!confirmed}
              size="lg"
              className="w-full h-14 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-lg font-semibold rounded-xl shadow-lg shadow-red-600/20 transition-all"
              data-testid="activate-now-btn"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Activate Your Approval
            </Button>
          </CardContent>
        </Card>

        {/* Option 2: Activate Later */}
        {discountActive && (
          <button
            onClick={onActivateLater}
            className="w-full text-center text-gray-500 hover:text-gray-300 text-sm py-3 transition-colors"
            data-testid="activate-later-btn"
          >
            Activate Later at Standard Terms (${fullFee.toLocaleString()})
          </button>
        )}

        {/* Trust Elements */}
        <div className="space-y-3 pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 text-gray-400 text-sm">
            <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span>Secure payment processing</span>
          </div>
          <div className="flex items-center gap-3 text-gray-400 text-sm">
            <Lock className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span>Trusted commercial financing provider</span>
          </div>
          {data?.funding_manager && (
            <div className="flex items-center gap-3 text-gray-400 text-sm">
              <Phone className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>
                Your funding manager: <span className="text-white font-medium">{data.funding_manager}</span>
                {data.funding_manager_phone && (
                  <a href={`tel:${data.funding_manager_phone}`} className="ml-2 text-red-400 hover:text-red-300 underline">
                    {data.funding_manager_phone}
                  </a>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Social proof */}
        <p className="text-center text-gray-600 text-xs">
          Most customers activate the same day
        </p>
      </div>
    </div>
  );
};

// ==================== STEP 3: LOI DETAILS (POST-ACTIVATION) ====================
const Step3LOIDetails = ({ data }) => {
  const terms = data?.terms || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        <div className="text-center space-y-2">
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            <CheckCircle className="w-4 h-4 mr-1" /> ACTIVATION COMPLETE
          </Badge>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Letter of Intent</h2>
          <p className="text-gray-400">Your financing terms</p>
        </div>

        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardContent className="p-6 space-y-6">
            {/* Equipment */}
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Equipment</p>
              <p className="text-white font-semibold">{data?.equipment_description}</p>
            </div>

            {/* Terms Table */}
            <div className="space-y-3">
              {[
                ['Equipment Cost', `$${(data?.equipment_cost || 0).toLocaleString()}`],
                ['Down Payment', `${terms.down_payment_pct}% ($${(terms.down_payment_amount || 0).toLocaleString()})`],
                ['Origination Fee', `$${(terms.origination_fee || 0).toLocaleString()}`],
                ['Term', `${terms.term_months} months`],
                terms.rate && ['Rate', `${terms.rate}%`],
                terms.monthly_payment && ['Est. Monthly Payment', `$${terms.monthly_payment.toLocaleString()}`],
                ['Program', terms.program_type || 'EFA'],
              ].filter(Boolean).map(([label, value], i) => (
                <div key={i} className="flex justify-between py-2 border-b border-white/5">
                  <span className="text-gray-400 text-sm">{label}</span>
                  <span className="text-white font-medium">{value}</span>
                </div>
              ))}
            </div>

            {/* Next Steps */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-emerald-400 font-medium mb-2">Next Steps</p>
              <ul className="text-gray-300 text-sm space-y-1">
                <li className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-400" /> Your conditional approval will be issued</li>
                <li className="flex items-center gap-2"><Clock className="w-3 h-3 text-gray-500" /> Upload required documents</li>
                <li className="flex items-center gap-2"><Clock className="w-3 h-3 text-gray-500" /> Final review and funding</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Funding Manager Contact */}
        {data?.funding_manager && (
          <div className="text-center space-y-2">
            <p className="text-gray-500 text-sm">Questions? Contact your funding manager</p>
            <p className="text-white font-medium">{data.funding_manager}</p>
            {data.funding_manager_phone && (
              <a href={`tel:${data.funding_manager_phone}`} className="inline-flex items-center gap-2 text-red-400 hover:text-red-300">
                <Phone className="w-4 h-4" /> {data.funding_manager_phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN ACTIVATION FLOW ====================
const ActivationFlow = () => {
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activating, setActivating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deals/public/activate/${token}`);
      setData(res.data);
      // If already activated, show step 3
      if (['activated', 'conditional_approval_issued', 'documents_pending', 'final_review', 'approved_for_funding', 'funded'].includes(res.data.status)) {
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleActivateNow = async () => {
    setActivating(true);
    try {
      // Step 1: Confirm activation
      await axios.post(`${API}/deals/public/activate/${token}/confirm`);
      // Step 2: Simulate payment completion (in production, this comes from PaySimple callback)
      await axios.post(`${API}/deals/public/activate/${token}/payment-complete`);
      setStep(3);
      await fetchData();
    } catch (err) {
      console.error('Activation error:', err);
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold text-white">Link Not Found</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {step === 1 && <Step1Approved data={data} onContinue={() => setStep(2)} />}
      {step === 2 && <Step2Payment data={data} onActivateNow={handleActivateNow} onActivateLater={() => setStep(2)} />}
      {step === 3 && <Step3LOIDetails data={data} />}
    </>
  );
};

export default ActivationFlow;
